import { View, Text, TouchableOpacity } from "react-native";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";
import LottieView from "lottie-react-native";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { getLottieSource } from "@/lib/lottie-registry";

/**
 * Weekend Banner Component
 * Displays the active weekend info with a custom Lottie animation
 * and colored border set by familie admins
 */
export function WeekendBanner() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend, roles } = useAuth();

  // Don't render if no active weekend
  if (!activeWeekend) return null;

  // Get banner settings with fallbacks
  const bannerColor = activeWeekend.banner_color || "#7C3AED";
  const lottieAnimation = activeWeekend.lottie_animation || "confetti";
  const lottieSource = getLottieSource(lottieAnimation) || getLottieSource("confetti");

  // Format dates
  const startDate = activeWeekend.start_date ? new Date(activeWeekend.start_date) : null;
  const endDate = activeWeekend.end_date ? new Date(activeWeekend.end_date) : null;
  const familieDagDate = activeWeekend.familie_dag_date ? new Date(activeWeekend.familie_dag_date) : null;
  
  const weekendDateRange =
    startDate && endDate
      ? `${format(startDate, "d MMM", { locale: nl })} - ${format(endDate, "d MMM yyyy", { locale: nl })}`
      : null;
  
  const familieDagFormatted = familieDagDate
    ? format(familieDagDate, "EEEE d MMMM", { locale: nl })
    : null;
    
  const isFinalized = activeWeekend.is_finalized;

  // Navigate to algemeen settings if admin taps
  const handlePress = () => {
    if (roles.isFamilieAdmin || roles.isSuperAdmin) {
      router.push("/(admin)/algemeen");
    }
  };

  const isAdmin = roles.isFamilieAdmin || roles.isSuperAdmin;

  return (
    <TouchableOpacity
      activeOpacity={isAdmin ? 0.8 : 1}
      onPress={isAdmin ? handlePress : undefined}
      className="mx-4 mt-4 rounded-2xl p-4 items-center"
      style={{
        backgroundColor: colors.surface,
        borderWidth: 3,
        borderColor: bannerColor,
      }}
      accessibilityRole={isAdmin ? "button" : "none"}
      accessibilityLabel={`Weekend banner: ${activeWeekend.name}`}
      accessibilityHint={isAdmin ? "Tik om instellingen te bewerken" : undefined}
    >
      {/* Lottie Animation */}
      <LottieView
        source={lottieSource}
        autoPlay
        loop
        style={{ width: 100, height: 100 }}
      />

      {/* Completed Badge */}
      {isFinalized && (
        <View
          className="absolute top-3 right-3 px-2 py-1 rounded-full flex-row items-center"
          style={{ backgroundColor: "#10B98120" }}
        >
          <Ionicons name="checkmark-circle" size={14} color="#10B981" />
          <Text className="text-xs font-medium ml-1" style={{ color: "#10B981" }}>
            Afgerond
          </Text>
        </View>
      )}

      {/* Weekend Name */}
      <Text
        className="text-xl font-bold text-center mt-2"
        style={{ color: colors.text }}
      >
        {activeWeekend.name}
      </Text>

      {/* Theme Badge */}
      {activeWeekend.theme && (
        <View
          className="mt-2 px-3 py-1 rounded-full"
          style={{ backgroundColor: `${bannerColor}20` }}
        >
          <Text
            className="text-sm font-medium"
            style={{ color: bannerColor }}
          >
            {activeWeekend.theme}
          </Text>
        </View>
      )}

      {/* Dates Section */}
      <View className="mt-4 w-full">
        {/* Familie Dag */}
        {familieDagFormatted && (
          <View className="flex-row items-center justify-center mb-2">
            <View
              className="w-6 h-6 rounded-full items-center justify-center mr-2"
              style={{ backgroundColor: `${bannerColor}20` }}
            >
              <Ionicons name="sunny" size={14} color={bannerColor} />
            </View>
            <View>
              <Text className="text-xs" style={{ color: colors.textTertiary }}>
                Familie Dag
              </Text>
              <Text className="text-sm font-medium" style={{ color: colors.text }}>
                {familieDagFormatted}
              </Text>
            </View>
          </View>
        )}
        
        {/* Familie Weekend */}
        {weekendDateRange && (
          <View className="flex-row items-center justify-center">
            <View
              className="w-6 h-6 rounded-full items-center justify-center mr-2"
              style={{ backgroundColor: `${bannerColor}20` }}
            >
              <Ionicons name="calendar" size={14} color={bannerColor} />
            </View>
            <View>
              <Text className="text-xs" style={{ color: colors.textTertiary }}>
                Familie Weekend
              </Text>
              <Text className="text-sm font-medium" style={{ color: colors.text }}>
                {weekendDateRange}
              </Text>
            </View>
          </View>
        )}
      </View>

      {/* Admin hint */}
      {isAdmin && (
        <Text
          className="text-xs mt-3"
          style={{ color: colors.textTertiary }}
        >
          Tik om te bewerken
        </Text>
      )}
    </TouchableOpacity>
  );
}

